import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { format, isValid } from "date-fns";
import { API_BASE } from "../../config";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
  Calendar
} from "lucide-react";
import { toast } from "react-hot-toast";

/* ----------------------- Helpers ----------------------- */

// Normalize PDF URL (Cloudinary / uploads)
const normalizeUrl = (url) => {
  if (!url) return "";
  if (String(url).startsWith("http")) return url;
  return `${API_BASE.replace(/\/+$/, "")}/${String(url).replace(/^\/+/, "")}`;
};

const normalizeId = (id) => {
  if (!id) return "";
  if (typeof id === "object" && id._id) return id._id.toString();
  return id.toString();
};

// Check if user has access to a plan (by purchasedPlans + not expired)
const userHasAccess = (user, planId) => {
  if (!user || !Array.isArray(user.purchasedPlans)) return false;
  const now = new Date();
  const target = normalizeId(planId);

  return user.purchasedPlans.some((p) => {
    const ownedId = normalizeId(p.planId);
    return ownedId === target && (!p.expiresAt || new Date(p.expiresAt) >= now);
  });
};

// Safe start date (prevents 1970 fallback)
const getBaseStartDate = (plan) => {
  const raw = plan?.startDate || plan?.createdAt;
  const d = raw ? new Date(raw) : null;
  return d && isValid(d) ? d : new Date();
};

/* ----------------------- Component ----------------------- */

const StudyPlans = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(1);  
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingPlanDetails, setLoadingPlanDetails] = useState(false);
  const [error, setError] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [activePlan, setActivePlan] = useState(null);

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });

  /* ----------------------- API utils ----------------------- */

  const getAuthHeaders = useCallback(() => {
    const token = (() => {
      if (user?.token) return user.token;
      try {
        return JSON.parse(localStorage.getItem("user"))?.token || "";
      } catch {
        return "";
      }
    })();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [user]);

  const saveUser = useCallback(
    (u) => {
      if (!u) return;
      const normalizedPlans =
        (u.purchasedPlans || []).map((p) => ({
          ...p,
          planId: p.planId?._id ? p.planId : { _id: p.planId },
        })) || [];

      const token = u.token || user?.token || (JSON.parse(localStorage.getItem("user") || "{}")?.token || "");
      const merged = { ...u, token, purchasedPlans: normalizedPlans };

      localStorage.setItem("user", JSON.stringify(merged));
      setUser(merged);
      window.dispatchEvent(new Event("userUpdated"));
    },
    [user?.token]
  );

  const fetchUserProfile = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) return null;

    try {
      const res = await axios.get(`${API_BASE}/api/user/profile`, { headers });
      if (!res?.data) return null;

      const backendUser = res.data;
      const token =
        user?.token ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem("user"))?.token || "";
          } catch {
            return "";
          }
        })();

      const updatedUser = {
        ...backendUser,
        token,
        purchasedPlans: backendUser.purchasedPlans || [],
      };

      saveUser(updatedUser);
      return updatedUser;
    } catch {
      return null;
    }
  }, [getAuthHeaders, saveUser, user?.token]);

  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    setError(null);
    const headers = getAuthHeaders();

    try {
      const res = await axios.get(
  `${API_BASE}/api/user/study-plans`,
  headers.Authorization ? { headers } : {}
);
      const fetched = Array.isArray(res.data) ? res.data : [];

      // Use backend hasAccess if provided, otherwise compute on client
      const normalized = fetched.map((p) => ({
        ...p,
        hasAccess: typeof p.hasAccess === "boolean" ? p.hasAccess : userHasAccess(user, p._id),
      }));

      setPlans(normalized);
    } catch (err) {
  console.error("Study plans fetch error:", err);

  // fallback: don't block UI
  setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  }, [getAuthHeaders, user]);

  const fetchPlanDetails = useCallback(
  async (planId) => {
    setLoadingPlanDetails(true);
    setError(null);
    const headers = getAuthHeaders();

    const toLocalDateOnly = (val) => {
      if (!val) return null;
      const d = new Date(val);
      if (isNaN(d)) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    };

    try {
      const res = await axios.get(`${API_BASE}/api/user/study-plans/${planId}`, { headers });
      const data = res?.data;
      if (!data) throw new Error("No plan data");

      const safeStart = toLocalDateOnly(data.startDate);
      if (!safeStart) throw new Error("Invalid start date in backend");

      const planData = { ...data, startDate: safeStart.toISOString() };
      setSelectedPlan(planData);
      setCurrentWeek(1);
      
    } catch (err) {
      console.error("fetchPlanDetails error:", err);
      setError("Unable to fetch plan details.");
    } finally {
      setLoadingPlanDetails(false);
    }
  },
  [getAuthHeaders]
);

const handleKnowMore = async (plan) => {
  try {
    const headers = getAuthHeaders();
    const res = await axios.get(`${API_BASE}/api/user/study-plans/${plan._id}`, { headers });
    const full = res.data;
    setActivePlan({
      ...plan,
      fullDescription: full.description || "",
    });
  } catch (err) {
    console.error("Failed to fetch plan details:", err);
    setActivePlan({ ...plan, fullDescription: "" });
  }
};

  /* ----------------------- Effects ----------------------- */

  useEffect(() => {   
    const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.async = true;
  document.body.appendChild(script);

    const init = async () => {
      // 1) ensure fresh user first
      await fetchUserProfile();
      // 2) then plans — so access reflects purchases
      await fetchPlans();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh plans whenever user is updated (e.g., post-payment)
  useEffect(() => {
    const handler = () => {
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "null");
        if (stored) setUser(stored);
      } catch {}
      setTimeout(() => {
        fetchPlans();
      }, 50);
    };
    window.addEventListener("userUpdated", handler);
    return () => window.removeEventListener("userUpdated", handler);
  }, [fetchPlans]);

  /* ----------------------- Purchase / open ----------------------- */

  const handlePlanSelect = async (plan) => {
    const access = (typeof plan.hasAccess === "boolean" ? plan.hasAccess : false) || userHasAccess(user, plan._id);
    if (!access) {
      await handlePlanPurchase(plan);
      return;
    }
    await fetchPlanDetails(plan._id);
  };

  const handlePlanPurchase = async (plan) => {
    if (!window.Razorpay) return alert("❌ Razorpay SDK not loaded.");

    const headers = getAuthHeaders();
    if (!headers.Authorization) return alert("⚠️ Please login to purchase.");

    try {
      const orderRes = await axios.post(
        `${API_BASE}/api/payments/create-order`,
        { amount: plan.price, planId: plan._id },
        { headers }
      );

      const order = orderRes?.data?.order;
      if (!order) throw new Error("Order not created.");

      const options = {
  key: process.env.REACT_APP_RAZORPAY_KEY,
  order_id: order.id,
  currency: order.currency,
  name: "UPSC Journey",
  description: `Purchase: ${plan.title || plan.durationLabel}`,
  prefill: {
    name: user?.username || "",
    email: user?.email || "",
  },
        handler: async (rzpRes) => {
          try {
            // Verify purchase
            const verifyRes = await axios.post(
              `${API_BASE}/api/payments/verify-payment`,
              {
                razorpay_order_id: rzpRes.razorpay_order_id,
                razorpay_payment_id: rzpRes.razorpay_payment_id,
                razorpay_signature: rzpRes.razorpay_signature,
                planId: plan._id,
              },
              { headers }
            );

            if (!verifyRes.data.success) {
              toast.error("Payment verification failed");
              return;
            }

            // =========================
// Refresh User From Backend
// =========================

const refreshedUser =
  await fetchUserProfile();



// Fallback if backend
// doesn't return instantly
if (!refreshedUser) {

  const updatedUser = {

    ...user,

    purchasedPlans: [

      ...(user?.purchasedPlans || []),

      {
        planId: plan._id,
      },
    ],
  };

  saveUser(updatedUser);
}



toast.success(
  "🎉 Plan unlocked successfully!"
);



// Return to list
setSelectedPlan(null);



// Refresh plans
setTimeout(() => {

  fetchPlans();

}, 300);
          } catch {
            toast.error("Payment verification error");
          }
        },
      };

      new window.Razorpay(options).open();
    } catch (err) {
      toast.error("Payment start failed");
    }
  };

  /* ----------------------- Render ----------------------- */

  if (loadingPlans) {
    return <p className="p-6 text-center text-gray-700 dark:text-gray-300">Loading study plans…</p>;
  }
  if (error) {
    return <div className="p-6 text-center text-red-600 dark:text-red-400">{error}</div>;
  }

  // List view
  if (!selectedPlan) {
    return (
      <div className="
min-h-screen
p-10
bg-[radial-gradient(circle_at_top_right,#2563eb22,transparent_35%),radial-gradient(circle_at_bottom_left,#7c3aed22,transparent_35%)]
dark:bg-black
">
        <h2 className="text-3xl font-bold text-center mb-10 text-gray-800 dark:text-gray-100">
          📘 Available Study Plans
        </h2>

        {plans.length === 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400">No study plans available yet.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const access = (typeof plan.hasAccess === "boolean" ? plan.hasAccess : false) || userHasAccess(user, plan._id);
            const safeStart = plan.startDate ? getBaseStartDate(plan) : null;

            return (
              <div
                key={plan._id}
                className="
group
relative
overflow-hidden
rounded-3xl
bg-white/80
dark:bg-gray-900/80
backdrop-blur-xl
border
border-white/20
dark:border-gray-700
shadow-[0_20px_60px_rgba(0,0,0,0.08)]
hover:shadow-[0_25px_80px_rgba(59,130,246,0.25)]
transition-all
duration-500
hover:-translate-y-2
"
              >
                {/* Top bar */}
                <div className="
relative
px-6
py-5
rounded-t-3xl
bg-gradient-to-br
from-[#0f172a]
via-[#1e40af]
to-[#7c3aed]
">
                  <h3 className="text-lg font-bold text-white truncate">
                    {plan.title || plan.durationLabel}
                  </h3>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      access
                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                        : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                    }`}
                  >
                    {access ? "Unlocked" : "Locked"}
                  </span>
                </div>

                {/* Card body */}
                <div
className="
p-6
flex
flex-col
min-h-[320px]
"
>
                  <div className="flex justify-between items-start mb-5">

<div>

<div className="
text-xs
uppercase
tracking-widest
text-gray-500
dark:text-gray-400
mb-2
">
Duration
</div>

<div className="
text-xl
font-bold
text-gray-900
dark:text-white
">
{plan.durationLabel}
</div>

<div className="
text-sm
text-blue-600
dark:text-blue-400
mt-1
">
{plan.durationInDays} Days
</div>

</div>

<div className="text-right">

{plan.originalPrice && (
<div
className="
text-gray-400
line-through
text-sm
"
>
₹{plan.originalPrice}
</div>
)}

<div
className="
text-3xl
font-black
bg-gradient-to-r
from-green-500
to-emerald-400
bg-clip-text
text-transparent
"
>
₹{plan.price}
</div>

{plan.originalPrice && (
<div
className="
inline-flex
mt-2
rounded-full
bg-red-100
dark:bg-red-900/40
px-3
py-1
text-red-600
text-xs
font-bold
"
>
SAVE {
Math.round(
(
(plan.originalPrice-plan.price)
/
plan.originalPrice
)*100
)
}
%
</div>
)}

</div>

</div>

                  {safeStart && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      📅 Starts on {format(safeStart, "dd MMM yyyy")}
                    </p>
                  )}

                  {plan.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 mb-4">
                      <span className="font-semibold">Structure of Delivery: </span>
                      {plan.description.substring(0, 150)}...
                    </div>
                  )}

                  <div
className="
mt-auto
pt-5
border-t
border-gray-100
dark:border-gray-700
flex
items-center
justify-between
gap-3
"
>

<button
onClick={() => handleKnowMore(plan)}
className="
flex-1
rounded-2xl
border
border-gray-200
dark:border-gray-700
bg-white
dark:bg-gray-800
py-3
font-semibold
text-gray-700
dark:text-gray-200
hover:border-blue-500
hover:text-blue-600
transition
"
>
View More
</button>

<button
onClick={() => handlePlanSelect(plan)}
className={`
flex-1
py-3
rounded-2xl
font-bold
text-white
shadow-lg
transition-all
duration-300
hover:scale-[1.02]

${
access
? "bg-gradient-to-r from-blue-600 to-indigo-600"

: "bg-gradient-to-r from-green-500 to-emerald-600"
}
`}
>

{access
? "Open Plan"

: "Buy Now"}

</button>

</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal */}
        {activePlan && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8 relative border border-gray-200 dark:border-gray-700"
    >
      {/* ❌ Close Button */}
      <button
        onClick={() => setActivePlan(null)}
        className="absolute top-3 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold"
      >
        ×
      </button>

      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 mb-2">
          {activePlan.title || activePlan.durationLabel}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
          A structured and strategic study plan tailored for your UPSC preparation journey
        </p>
      </div>

      {/* Info Badges */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm">
          ⏳ Duration: {activePlan.durationInDays} Days
        </span>
        <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm">
          💰

{activePlan.originalPrice && (
<>
<span className="line-through opacity-60">
₹{activePlan.originalPrice}
</span>

{" → "}
</>
)}

₹{activePlan.price}
        </span>
        {activePlan.startDate && (
          <span className="bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm">
            📅 Starts: {format(new Date(activePlan.startDate), "dd MMM yyyy")}
          </span>
        )}
      </div>

      {/* 🌟 AI-STYLED, PREMIUM PLAN OVERVIEW (AUTO FORMATTED) */}
<div className="bg-white dark:bg-gray-900 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 mb-10">

  <h4 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">
    📘 Study Plan Structure
  </h4>

  {(() => {
    const desc = activePlan.fullDescription || activePlan.description || "";

    if (!desc.trim()) {
      return <p className="text-gray-500 italic">No description available.</p>;
    }

    // Split by sections using ---
    const sections = desc.split(/---+/);

    return sections.map((section, index) => {
      const lines = section.trim().split("\n").filter(Boolean);

      if (lines.length === 0) return null;

      const title = lines[0];
      const content = lines.slice(1);

      return (
        <div key={index} className="mb-8">

          {/* Section Title */}
          <h5 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-3">
            {title}
          </h5>

          {/* Content */}
          <div className="space-y-2 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">

            {content.map((line, i) => {
              // Bullet points
              if (line.startsWith("*") || line.startsWith("•")) {
                return (
                  <div key={i} className="ml-4 flex gap-2">
                    <span>•</span>
                    <span>{line.replace(/^[*•]\s*/, "")}</span>
                  </div>
                );
              }

              // Headings like 👉
              if (line.startsWith("👉")) {
                return (
                  <div key={i} className="font-medium text-gray-900 dark:text-gray-200">
                    {line}
                  </div>
                );
              }

              // Normal text
              return <p key={i}>{line}</p>;
            })}

          </div>

          {/* Divider */}
          <div className="mt-6 border-b border-gray-200 dark:border-gray-700" />
        </div>
      );
    });
  })()}
</div>

      {/* Buttons */}
      <div className="flex justify-center gap-4 mt-4">
        <button
          onClick={() => {
            handlePlanSelect(activePlan);
            setActivePlan(null);
          }}
          className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:opacity-90 hover:shadow-blue-500/30 transition-all"
        >
          {activePlan.hasAccess || userHasAccess(user, activePlan._id)
            ? "View Plan"
            : "Buy Now"}
        </button>

        <button
          onClick={() => setActivePlan(null)}
          className="px-6 py-2.5 rounded-lg font-semibold border border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
        >
          Close
        </button>
      </div>
    </motion.div>
  </div>
)}
      </div>
    );
  }

  /* -------- Selected plan view (collapsible weeks/days) -------- */

  const maxWeek = Math.ceil(selectedPlan.durationInDays / 7) || 1;
  
  const getDailyPdfsForWeek = (weekNumber) => {
  return (selectedPlan?.content || [])
    .filter(
      (pdf) =>
        pdf.day &&
        Number(pdf.week) === weekNumber &&
        pdf.scheduledDate
    )
    .sort(
      (a, b) =>
        new Date(a.scheduledDate) -
        new Date(b.scheduledDate)
    );
};  

  const weeklyPdfsByWeek = (weekNumber) =>
    (selectedPlan.content || [])
      .filter((pdf) => pdf.week && Number(pdf.week) === weekNumber && !pdf.day)
      .map((pdf) => ({ ...pdf, url: normalizeUrl(pdf.url) }));

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen max-w-7xl mx-auto">
      <button
        onClick={async () => {
          setSelectedPlan(null);
          setCurrentWeek(1);          
          // Force refresh plan access when returning
          await fetchPlans();
        }}
        className="mb-6 text-blue-600 hover:underline flex items-center gap-2 font-medium"
      >
        ← Back to all plans
      </button>

      <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">
        {selectedPlan.durationLabel}
      </h2>

      <div className="space-y-6">
        {Array.from({ length: maxWeek }).map((_, weekIdx) => {
          const weekNumber = weekIdx + 1;
          const dailyPdfs = getDailyPdfsForWeek(weekNumber);
          const weeklyPdfs = weeklyPdfsByWeek(weekNumber);

          return (
            <motion.div
              key={weekNumber}
              className="bg-white border border-gray-200 rounded-2xl shadow-md overflow-hidden"
            >
              {/* Week header */}
              <div
                onClick={() => setExpandedWeek(expandedWeek === weekNumber ? null : weekNumber)}
                className="flex justify-between items-center cursor-pointer px-6 py-4 bg-gray-100 hover:bg-gray-200 transition"
              >
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Week {weekNumber}
                </h3>
                {expandedWeek === weekNumber ? (
                  <ChevronUp className="w-5 h-5 text-gray-700" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-700" />
                )}
              </div>

              {/* Expanded content */}
              <AnimatePresence initial={false}>
                {expandedWeek === weekNumber && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 py-5 space-y-6"
                  >
                    {/* Weekly PDFs */}
                    {weeklyPdfs.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-700 mb-3">📘 Weekly PDFs</h4>
                        <ul className="space-y-3">
                          {weeklyPdfs.map((pdf) => (
                            <li
                              key={pdf._id || pdf.title}
                              className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                            >
                              <span className="flex items-center gap-2 font-medium text-gray-800">
                                <FileText className="w-4 h-4 text-blue-600" />
                                {pdf.title}
                              </span>
                              <div className="space-x-4">
                                <a
                                  href={pdf.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline font-medium"
                                >
                                  View
                                </a>
                                <a href={pdf.url} download className="text-green-600 hover:underline font-medium">
                                  <Download className="inline-block w-4 h-4 mr-1" />
                                  Download
                                </a>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Daily PDFs */}
                    <div className="space-y-5">
                      {dailyPdfs.length === 0 ? (
  <p className="text-gray-500 italic">
    No daily PDFs available
  </p>
) : (
  dailyPdfs.map((pdf) => {
    const pdfDate = new Date(pdf.scheduledDate);

    const dayLocked =
      format(pdfDate, "yyyy-MM-dd") >
      format(new Date(), "yyyy-MM-dd");

    return (
      <div
        key={pdf._id}
        className="p-5 rounded-xl border border-gray-200 bg-gray-50"
      >
        <h4 className="text-md font-semibold mb-2 text-gray-800 flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-500" />
          Day {pdf.day} —{" "}
          {format(pdfDate, "eeee, dd MMM yyyy")}
        </h4>

        {dayLocked ? (
          <p className="text-gray-400 italic">
            🔒 Content available on schedule
          </p>
        ) : (
          <ul className="space-y-3">
            <li className="flex justify-between items-center p-3 bg-white rounded-lg">
              <span className="flex items-center gap-2 font-medium text-gray-800">
                <FileText className="w-4 h-4 text-blue-600" />
                {pdf.title}
              </span>

              <div className="space-x-4">
                <a
                  href={pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline font-medium"
                >
                  View
                </a>

                <a
                  href={pdf.url}
                  download
                  className="text-green-600 hover:underline font-medium"
                >
                  <Download className="inline-block w-4 h-4 mr-1" />
                  Download
                </a>
              </div>
            </li>
          </ul>
        )}
      </div>
    );
  })
)}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default StudyPlans;
